/** @jsx jsx */
import { Global, css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import { useLocation, useNavigate } from 'js/lib/useRouter';

import { breakpoints } from '@coursera/cds-core';

/**
 * Returns true if `target` is a close-affordance for one of the modals we're
 * restyling: the legacy `.c-modal-x-out` button, or any <button> inside the
 * first child div of a `.cds-Dialog-dialog` (the CDS dialog header row).
 */
const isModalCloseTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false;

  // Legacy Modal
  if (target.closest('.c-modal-x-out a')) return true;
  // CDS Modal
  if (target.closest('[class*="-Modal--closeBtn"]')) return true;

  // eslint-disable-next-line no-restricted-syntax
  if (target.closest('[data-e2e="close-modal-button"]')) return true;

  return false;
};

/**
 * Attaches a document-level click listener that strips `bgStyle` from the URL
 * when the user clicks a modal close button. Only active while the experiment
 * is on, so it's a no-op for the vast majority of users.
 */
const useClearBgStyleOnModalClose = (active: boolean) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!active) return undefined;

    const handleClick = (event: MouseEvent) => {
      if (!isModalCloseTarget(event.target)) return;

      const params = new URLSearchParams();
      Object.entries(location.query ?? {}).forEach(([key, value]) => {
        if (key !== 'bgStyle' && value != null) {
          params.set(key, String(value));
        }
      });

      navigate({
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      });
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [active, location.pathname, location.query, navigate]);
};

const enrollActionBackgroundCss = css`
  /* Remove scrolling to give the impression that we're not hiding a page */
  body.c-phoenix-modal-open {
    overflow: hidden !important;
  }

  /* CDS Login Modal */
  .cds-Modal-container {
    position: absolute;
    top: 105px !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    background: var(--cds-color-emphasis-neutral-background-weak) !important;
    z-index: 1 !important;
    opacity: 1 !important;

    &::after {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: -1;
      content: '';

      /* legacy modal styles aren't in CDS */
      /* stylelint-disable-next-line */
      background: rgba(99, 99, 99, 0.5);
    }
  }

  /* Free trial legacy modal */
  #rc-TopLevelModal {
    position: relative;
    z-index: 5000;
  }

  .c-modal-overlay {
    top: 105px !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    background: var(--cds-color-emphasis-neutral-background-weak) !important;
    z-index: 1 !important;
    opacity: 1 !important;

    &::after {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: -1;
      content: '';

      /* legacy modal styles aren't in CDS */
      /* stylelint-disable-next-line */
      background: rgba(99, 99, 99, 0.5);
    }
  }

  ${breakpoints.down('sm')} {
    .c-modal-overlay {
      top: 65px !important;
    }
  }
`;

/**
 * Experiment: injects a global background style targeting modal/dialog DOM
 * nodes when the URL has `?action=enroll&bgStyle=simple`. Styles are injected
 * via Helmet so they reach modal/dialog elements that render via portals
 * outside the React tree. Designed for small blast radius — to clean up,
 * delete this file and remove the call site.
 */
export const EnrollActionBackground = () => {
  const location = useLocation();
  const isSimpleBgStyle = location?.query?.bgStyle === 'simple';
  const isEnrollAction = location?.query?.action === 'enroll';
  useClearBgStyleOnModalClose(isSimpleBgStyle && isEnrollAction);

  if (!isSimpleBgStyle || !isEnrollAction) {
    return null;
  }

  return <Global styles={enrollActionBackgroundCss} />;
};

export default EnrollActionBackground;
